/**
 * "This job can't go out yet" — the job page's heads-up from the check-up
 * (#1669, mockup screen 1). Shows before anyone asks, NAMES the thing in the
 * way, and opens the Help Desk on it. Renders nothing when the check-up finds
 * nothing, is still loading, or can't be read — a strip that guesses would be
 * worse than none.
 *
 * It names the blocker itself (2026-10-28, Discovery Village #082669055). It
 * used to say only "One thing to fix. The Help Desk found it." — right above
 * the orange city-registration card, so Corbin went chasing an insurance
 * certificate while the real hold was the bypass's city report. Registration
 * is warn-only and never holds a job (Corbin, 10/6): it is never in this
 * strip, and its card says "Heads up".
 */
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { openHelpDesk, useJobCheckup, type CheckupProblem } from "@/lib/use-job-checkup";

/** A title as a sentence: "… hasn't been filed with the city." */
function sentence(title: string): string {
    const t = title.trim();
    return /[.!?]$/.test(t) ? t : `${t}.`;
}

/**
 * What the strip says. Pure, for tests. The lead is the first problem the
 * office can fix (else the first one), named in its own words; the rest are a
 * count. `totalCount` is the page's whole "What needs you" list, when known.
 */
export function stripWords(
    problems: Pick<CheckupProblem, "severity" | "title">[],
    totalCount?: number,
): { headline: string; sub: string } {
    const officeCanFix = problems.some((p) => p.severity === "blocked");
    const lead = (officeCanFix ? problems.find((p) => p.severity === "blocked") : undefined) ?? problems[0];
    const headline = officeCanFix ? "This job can't go out yet." : "This job is waiting on someone else.";
    const n = Math.max(totalCount ?? problems.length, problems.length);
    const others = n - 1;
    const rest =
        others <= 0
            ? ""
            : totalCount !== undefined
              ? others === 1
                  ? " 1 more thing is listed below."
                  : ` ${others} more things are listed below.`
              : others === 1
                ? " Show me has 1 more."
                : ` Show me has ${others} more.`;
    return { headline, sub: `${lead ? sentence(lead.title) : ""}${rest}` };
}

export function JobCheckupStrip({
    jobId,
    totalCount,
}: {
    jobId: string;
    /** How many rows the NOW panel's "What needs you" lists (check-up rows +
     *  the page's own derived blockers, from `useJobNeeds`). When given, the
     *  strip says that number, so "N things in the way" always matches the
     *  list under it (audit 2026-10-01: "One thing to fix" over two rows). */
    totalCount?: number;
}) {
    const { can } = useAuth();
    const allowed = can(PERMISSIONS.officeReview.manage);
    const q = useJobCheckup(jobId, allowed);
    const problems = q.data?.problems ?? [];
    if (!allowed || problems.length === 0) return null;

    const { headline, sub } = stripWords(problems, totalCount);

    return (
        <div
            data-testid="job-checkup-strip"
            role="status"
            className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 basis-[calc(100%-2rem)] sm:basis-auto">
                <span className="font-semibold">{headline}</span> {sub}
            </span>
            {/* Own line on a phone, left-aligned: the page's floating + sits bottom-right. */}
            <Button size="sm" variant="outline" className="ml-6 sm:ml-0" onClick={openHelpDesk}>
                Show me
            </Button>
        </div>
    );
}
